import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Translate } from 'react-localize-redux';
import moment from 'moment';
import { NewTourEvent } from '@common/types/traclink/new-tour-event';
import InputField from '@components/form/input';
import Datepicker from '@components/form/datepicker';
import Button from '@components/actions/button';
import Flex from '@components/layout/flex';
import { motion } from 'framer-motion';
import Typography from '@components/basics/typography';

const opacity = {
  visible: {
    opacity: 1,
    scaleY: 1,
    originY: 0,
    transition: {
      ease: [0.0, 0.0, 0.25, 1.0],
      duration: 0.5,
    },
  },
  hidden: {
    opacity: 0,
    scaleY: 0,
  },
};

interface NewTourProps {
  className?: string;
  onAdd: (newTour: Omit<NewTourEvent, 'ownerId'>) => void;
  onCancel: () => void;
  loading: boolean;
}

const NewTour: FunctionComponent<NewTourProps> = ({
  className,
  onAdd,
  onCancel,
  loading,
}) => {
  const [eventName, setEventName] = React.useState<string>('');
  const [place, setPlace] = React.useState<string>('');
  const [date, setDate] = React.useState<string>('');
  const [link, setLink] = React.useState<string>('');
  const [error, setError] = React.useState<string>('');

  const [activeDate] = React.useState(moment(new Date()));

  React.useEffect(() => {
    setError('');
  }, [eventName, place, date, link]);

  const getMinDate = () => moment().startOf('day').add(2, 'days');

  const handleAddTour = () => {
    if (link && !link.match(/^(http|https):\/\//)) {
      setError('ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.LINK_ERROR');
      return;
    }
    if (eventName && place && date && link) {
      onAdd({
        title: eventName,
        place: place,
        dateTime: new Date(date),
        link,
      });
    } else {
      setError('ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.ERROR');
    }
  };

  return (
    <Wrapper
      className={className || ''}
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={opacity}
    >
      <InputField
        label={
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.EVENT_NAME" />
        }
        value={eventName}
        onChange={(value) => setEventName(value)}
      />
      {/* TODO: DEVTEAM-845 - Add autocomplete with locations from google maps */}
      <InputField
        label={
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.CITY" />
        }
        value={place}
        onChange={(value) => setPlace(value)}
      />
      <Datepicker
        title={
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.DATE" />
        }
        value={date ?? moment(date).format('MMMM DD, YYYY')}
        active={activeDate}
        min={getMinDate()}
        onChange={(value) => setDate(moment(value).format('MMMM DD, YYYY'))}
      />
      <InputField
        label={
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.LINK" />
        }
        value={link}
        onChange={(value) => setLink(value)}
      />
      {error && (
        <ErrorTypography type="small" weight="700">
          <Translate id={error} />
        </ErrorTypography>
      )}
      <Flex>
        <Button buttonType="secondary" onClick={onCancel} fullWidth>
          <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.HIDE.LABEL" />
        </Button>
        <Button buttonType="secondary-dark" onClick={handleAddTour} fullWidth>
          {loading ? (
            <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.LOADING.LABEL" />
          ) : (
            <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TOUR.INPUT.ADD.LABEL" />
          )}
        </Button>
      </Flex>
    </Wrapper>
  );
};

export default NewTour;

const Wrapper = styled(motion.div)`
  display: grid;
  grid-gap: 10px;
`;

const ErrorTypography = styled(Typography)`
  color: ${({ theme }) => theme.colors.error};
  margin: 0 4px;
`;
